import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, DollarSign, TrendingUp, TrendingDown,
  Activity, Users, Store, CreditCard, AlertTriangle, CheckCircle,
  Download, Info, ChevronDown, Calendar, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

// Date presets
const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'year_to_date', label: 'Year to Date' },
  { value: 'all_time', label: 'All Time' },
];

// Format currency
const formatCurrency = (value, showSign = false) => {
  if (value === null || value === undefined) return '$0.00';
  const prefix = showSign && value > 0 ? '+' : '';
  return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00%';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

// Data source badge
const DataSourceBadge = ({ source }) => {
  const colors = {
    actual: 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
    estimated: 'bg-amber-900/50 text-amber-300 border-amber-700',
    simulated: 'bg-purple-900/50 text-purple-300 border-purple-700'
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase font-medium ${colors[source] || colors.simulated}`}>
      {source}
    </span>
  );
};

// KPI Card component
const KPICard = ({ title, value, subValue, change, source, tooltip, icon: Icon, colorClass = 'text-white' }) => (
  <TooltipProvider>
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 relative">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${colorClass}`} />}
          <span className="text-slate-400 text-xs font-medium">{title}</span>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="w-3 h-3 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {source && <DataSourceBadge source={source} />}
      </div>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {subValue && <span className="text-slate-500 text-xs">{subValue}</span>}
        {change !== null && change !== undefined && (
          <span className={`text-xs flex items-center gap-0.5 ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {formatPercent(change)}
          </span>
        )}
      </div>
    </div>
  </TooltipProvider>
);

// Mini stat component
const MiniStat = ({ label, value, tooltip }) => (
  <div className="text-center p-3 bg-slate-900/50 rounded-lg">
    <TooltipProvider>
      <div className="flex items-center justify-center gap-1 mb-1">
        <span className="text-slate-400 text-xs">{label}</span>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="w-3 h-3 text-slate-500" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{tooltip}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
    <p className="text-white font-semibold">{value}</p>
  </div>
);

export default function AdminFinancialAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [datePreset, setDatePreset] = useState('last_30_days');
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/analytics/financial?date_preset=${datePreset}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        if (response.status === 403) {
          navigate('/admin');
          return;
        }
        throw new Error('Failed to fetch financial analytics');
      }

      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error fetching financial analytics:', error);
      toast.error('Failed to load financial analytics');
    } finally {
      setLoading(false);
    }
  }, [navigate, isAdmin, datePreset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const token = getToken();
    setExporting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/analytics/financial/export?date_preset=${datePreset}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const exportData = await response.json();
      
      // Convert to CSV
      const headers = exportData.headers.join(',');
      const rows = exportData.rows.map(row => 
        exportData.headers.map(h => `"${row[h] || ''}"`).join(',')
      );
      const csv = [headers, ...rows].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pecash-financial-${datePreset}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const kpis = data?.primary_kpis;
  const unit = data?.unit_economics;
  const flow = data?.money_flow;
  const profit = data?.profitability;
  const auth = data?.authorization_analytics;
  const sender = data?.sender_economics;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/analytics')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Financial Analytics</h1>
                <p className="text-sm text-slate-400">Revenue, fees, transaction volume, costs, and unit economics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {data?.pilot_mode && (
                <span className="px-3 py-1 bg-amber-900/50 text-amber-300 text-xs rounded-full border border-amber-700">
                  PILOT MODE
                </span>
              )}
              <Button
                onClick={handleExport}
                variant="outline"
                className="border-slate-600 text-slate-300"
                disabled={exporting || loading}
              >
                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                Export CSV
              </Button>
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-slate-600 text-slate-300"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-4">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="w-[200px] bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_PRESETS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {data?.period && (
              <span className="text-slate-400 text-sm">
                {new Date(data.period.start).toLocaleDateString()} - {new Date(data.period.end).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-10 h-10 text-purple-400 animate-spin" />
          </div>
        ) : data ? (
          <div className="space-y-8">
            {/* PRIMARY KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <KPICard
                title="Transactions"
                value={kpis?.transactions?.value?.toLocaleString() || 0}
                change={kpis?.transactions?.change_pct}
                source={kpis?.transactions?.source}
                tooltip={kpis?.transactions?.tooltip}
                icon={Activity}
                colorClass="text-blue-400"
              />
              <KPICard
                title="Transaction Volume (GMV)"
                value={formatCurrency(kpis?.gmv?.value_usd)}
                subValue={`RD$ ${(kpis?.gmv?.value_dop || 0).toLocaleString()}`}
                change={kpis?.gmv?.change_pct}
                source={kpis?.gmv?.source}
                tooltip={kpis?.gmv?.tooltip}
                icon={DollarSign}
                colorClass="text-slate-300"
              />
              <KPICard
                title="PeCash Service Fees"
                value={formatCurrency(kpis?.pecash_revenue?.value)}
                change={kpis?.pecash_revenue?.change_pct}
                source={kpis?.pecash_revenue?.source}
                tooltip={kpis?.pecash_revenue?.tooltip}
                icon={TrendingUp}
                colorClass="text-emerald-400"
              />
              <KPICard
                title="Processing Fees"
                value={formatCurrency(-kpis?.processing_fees?.value)}
                change={kpis?.processing_fees?.change_pct}
                source={kpis?.processing_fees?.source}
                tooltip={kpis?.processing_fees?.tooltip}
                icon={CreditCard}
                colorClass="text-red-400"
              />
              <KPICard
                title="Other Costs"
                value={formatCurrency(-kpis?.other_direct_costs?.value)}
                source={kpis?.other_direct_costs?.source}
                tooltip={kpis?.other_direct_costs?.tooltip}
                icon={AlertTriangle}
                colorClass="text-orange-400"
              />
              <KPICard
                title="Net Transaction Profit"
                value={formatCurrency(kpis?.net_profit?.value)}
                change={kpis?.net_profit?.change_pct}
                source={kpis?.net_profit?.source}
                tooltip={kpis?.net_profit?.tooltip}
                icon={CheckCircle}
                colorClass={kpis?.net_profit?.value >= 0 ? 'text-emerald-400' : 'text-red-400'}
              />
            </div>

            {/* UNIT ECONOMICS ROW */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-400" />
                Unit Economics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <MiniStat label="Avg Transaction" value={formatCurrency(unit?.avg_transaction_value)} />
                <MiniStat label="Avg PeCash Fee" value={formatCurrency(unit?.avg_pecash_fee)} />
                <MiniStat label="Avg Processing Cost" value={formatCurrency(unit?.avg_processing_cost)} />
                <MiniStat label="Profit / Transaction" value={formatCurrency(unit?.avg_profit_per_transaction)} />
                <MiniStat label="Take Rate" value={`${unit?.take_rate_pct || 0}%`} tooltip={unit?.tooltips?.take_rate} />
                <MiniStat label="Processing Rate" value={`${unit?.processing_cost_rate_pct || 0}%`} />
                <MiniStat label="Net Margin" value={`${unit?.net_margin_pct || 0}%`} tooltip={unit?.tooltips?.net_margin} />
                <MiniStat label="Profit % of GMV" value={`${unit?.profit_as_pct_gmv || 0}%`} tooltip={unit?.tooltips?.profit_as_pct_gmv} />
              </div>
            </div>

            {/* MONEY FLOW / PROFIT WATERFALL */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Money Flow / Profit Waterfall
              </h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center">
                <div className="bg-slate-700 rounded-lg px-4 py-3 min-w-[140px]">
                  <p className="text-slate-400 text-xs">Customer GMV</p>
                  <p className="text-slate-300 font-bold">{formatCurrency(flow?.customer_gmv)}</p>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-500 rotate-0 md:-rotate-90" />
                <div className="bg-emerald-900/30 border border-emerald-800 rounded-lg px-4 py-3 min-w-[140px]">
                  <p className="text-emerald-400 text-xs">PeCash Fees</p>
                  <p className="text-emerald-300 font-bold">+{formatCurrency(flow?.pecash_fees_collected)}</p>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-500 rotate-0 md:-rotate-90" />
                <div className="bg-red-900/30 border border-red-800 rounded-lg px-4 py-3 min-w-[140px]">
                  <p className="text-red-400 text-xs">Processing Fees</p>
                  <p className="text-red-300 font-bold">{formatCurrency(flow?.processing_fees)}</p>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-500 rotate-0 md:-rotate-90" />
                <div className="bg-orange-900/30 border border-orange-800 rounded-lg px-4 py-3 min-w-[140px]">
                  <p className="text-orange-400 text-xs">Other Costs</p>
                  <p className="text-orange-300 font-bold">{formatCurrency(flow?.other_direct_costs)}</p>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-500 rotate-0 md:-rotate-90" />
                <div className={`rounded-lg px-4 py-3 min-w-[140px] ${flow?.net_transaction_profit >= 0 ? 'bg-emerald-900/50 border border-emerald-700' : 'bg-red-900/50 border border-red-700'}`}>
                  <p className={`text-xs ${flow?.net_transaction_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>Net Profit</p>
                  <p className={`font-bold ${flow?.net_transaction_profit >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                    ={formatCurrency(flow?.net_transaction_profit)}
                  </p>
                </div>
              </div>
            </div>

            {/* TRANSACTION SIZE ECONOMICS */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Profitability by Transaction Size
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                      <th className="pb-3 pr-4">Size Bucket</th>
                      <th className="pb-3 pr-4 text-right">Transactions</th>
                      <th className="pb-3 pr-4 text-right">GMV</th>
                      <th className="pb-3 pr-4 text-right">PeCash Revenue</th>
                      <th className="pb-3 pr-4 text-right">Processing Cost</th>
                      <th className="pb-3 pr-4 text-right">Net Profit</th>
                      <th className="pb-3 pr-4 text-right">Profit/Tx</th>
                      <th className="pb-3 text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.transaction_size_economics?.map((bucket, i) => (
                      <tr key={i} className={`border-b border-slate-700/50 ${!bucket.is_profitable && bucket.transaction_count > 0 ? 'bg-red-900/10' : ''}`}>
                        <td className="py-3 pr-4 text-slate-300 font-medium">{bucket.bucket}</td>
                        <td className="py-3 pr-4 text-right text-slate-300">{bucket.transaction_count}</td>
                        <td className="py-3 pr-4 text-right text-slate-300">{formatCurrency(bucket.gmv)}</td>
                        <td className="py-3 pr-4 text-right text-emerald-400">{formatCurrency(bucket.pecash_revenue)}</td>
                        <td className="py-3 pr-4 text-right text-red-400">-{formatCurrency(bucket.processing_cost)}</td>
                        <td className={`py-3 pr-4 text-right font-medium ${bucket.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(bucket.net_profit)}
                        </td>
                        <td className={`py-3 pr-4 text-right ${bucket.profit_per_transaction >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(bucket.profit_per_transaction)}
                        </td>
                        <td className={`py-3 text-right ${bucket.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {bucket.net_margin.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PROFITABLE VS UNPROFITABLE */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Transaction Profitability
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Profitable Transactions</span>
                    <span className="text-emerald-400 font-bold">{profit?.profitable_transactions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Unprofitable Transactions</span>
                    <span className="text-red-400 font-bold">{profit?.unprofitable_transactions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-700 pt-4">
                    <span className="text-slate-400">Profitable Rate</span>
                    <span className="text-white font-bold">{profit?.profitable_pct || 0}%</span>
                  </div>
                  {profit?.total_loss_from_unprofitable > 0 && (
                    <div className="flex items-center justify-between text-red-400">
                      <span>Total Loss from Unprofitable</span>
                      <span className="font-bold">-{formatCurrency(profit?.total_loss_from_unprofitable)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* SUPPORTER ECONOMICS */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Supporter Economics
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Active Supporters</span>
                    <span className="text-white font-bold">{sender?.active_senders || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Repeat Supporters</span>
                    <span className="text-white">{sender?.repeat_senders || 0} ({sender?.repeat_rate_pct || 0}%)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Authorizations / Supporter</span>
                    <span className="text-white">{sender?.transactions_per_sender?.toFixed(1) || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Avg GMV / Supporter</span>
                    <span className="text-white">{formatCurrency(sender?.avg_sender_gmv)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Avg Profit / Supporter</span>
                    <span className="text-emerald-400 font-bold">{formatCurrency(sender?.avg_profit_per_sender)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AUTHORIZATION ANALYTICS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-amber-400" />
                  Authorization Performance
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Total Authorizations</span>
                    <span className="text-white font-bold">{auth?.total_authorizations || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Total Authorized Value</span>
                    <span className="text-white">{formatCurrency(auth?.total_authorized_value)}</span>
                  </div>
                  {auth?.by_status && Object.entries(auth.by_status).map(([status, stats]) => (
                    <div key={status} className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 capitalize">{status.replace(/_/g, ' ')}</span>
                      <span className="text-slate-400">{stats.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* OUTSTANDING AUTHORIZATIONS */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Outstanding Authorizations
                  <span className="text-xs text-slate-500 font-normal">(Liability)</span>
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Outstanding Count</span>
                    <span className="text-amber-400 font-bold">{auth?.outstanding?.count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Outstanding Value (USD)</span>
                    <span className="text-amber-400 font-bold">{formatCurrency(auth?.outstanding?.value_usd)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Outstanding Value (DOP)</span>
                    <span className="text-slate-300">RD$ {(auth?.outstanding?.value_dop || 0).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3 mt-3">
                    <p className="text-slate-400 text-xs mb-2">Age Distribution:</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {auth?.outstanding?.age_buckets && Object.entries(auth.outstanding.age_buckets).map(([bucket, count]) => (
                        <div key={bucket} className="bg-slate-900/50 rounded px-2 py-1 text-center">
                          <span className="text-slate-500">{bucket}d:</span>
                          <span className="text-white ml-1">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CANCELLED & RELEASED */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-red-400" />
                Cancelled & Released Funds
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-400 text-xs mb-1">Cancelled Authorizations</p>
                  <p className="text-white font-bold text-xl">{auth?.cancelled_released?.cancelled_count || 0}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-400 text-xs mb-1">Original Value</p>
                  <p className="text-slate-300 font-bold">{formatCurrency(auth?.cancelled_released?.original_value)}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-400 text-xs mb-1">Released to Supporters</p>
                  <p className="text-emerald-400 font-bold">{formatCurrency(auth?.cancelled_released?.released_to_senders)}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-slate-400 text-xs mb-1">Avg Released</p>
                  <p className="text-slate-300 font-bold">{formatCurrency(auth?.cancelled_released?.avg_released)}</p>
                </div>
              </div>
            </div>

            {/* MERCHANT ECONOMICS */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-400" />
                Top Merchants / Colmados
              </h2>
              {data?.merchant_economics?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-700">
                        <th className="pb-3 pr-4">Merchant</th>
                        <th className="pb-3 pr-4 text-right">Transactions</th>
                        <th className="pb-3 pr-4 text-right">GMV</th>
                        <th className="pb-3 pr-4 text-right">PeCash Revenue</th>
                        <th className="pb-3 pr-4 text-right">Costs</th>
                        <th className="pb-3 pr-4 text-right">Net Profit</th>
                        <th className="pb-3 text-right">Avg Tx</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.merchant_economics.map((m, i) => (
                        <tr key={i} className="border-b border-slate-700/50">
                          <td className="py-3 pr-4">
                            <p className="text-white font-medium">{m.merchant_name}</p>
                            <p className="text-slate-500 text-xs">{m.merchant_id?.slice(0, 8)}...</p>
                          </td>
                          <td className="py-3 pr-4 text-right text-slate-300">{m.transaction_count}</td>
                          <td className="py-3 pr-4 text-right text-slate-300">{formatCurrency(m.gmv)}</td>
                          <td className="py-3 pr-4 text-right text-emerald-400">{formatCurrency(m.pecash_revenue)}</td>
                          <td className="py-3 pr-4 text-right text-red-400">-{formatCurrency(m.direct_costs)}</td>
                          <td className={`py-3 pr-4 text-right font-medium ${m.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(m.net_profit)}
                          </td>
                          <td className="py-3 text-right text-slate-300">{formatCurrency(m.avg_transaction)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No merchant data for selected period</p>
              )}
            </div>

            {/* RECONCILIATION STATUS */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-white">Reconciliation Status</h2>
                  {data?.reconciliation?.status === 'reconciled' ? (
                    <span className="px-3 py-1 bg-emerald-900/50 text-emerald-300 text-xs rounded-full border border-emerald-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Reconciled
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-amber-900/50 text-amber-300 text-xs rounded-full border border-amber-700 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Discrepancy
                    </span>
                  )}
                </div>
                <div className="text-right text-sm">
                  <p className="text-slate-400">Transactions: <span className="text-white">{data?.reconciliation?.transaction_count_verified}</span></p>
                  <p className="text-slate-400">GMV: <span className="text-white">{formatCurrency(data?.reconciliation?.gmv_verified)}</span></p>
                </div>
              </div>
            </div>

            {/* DATA SOURCES FOOTER */}
            <div className="bg-slate-900/50 rounded-xl p-4 flex flex-wrap items-center gap-4 text-xs">
              <span className="text-slate-400">Data Sources:</span>
              <span className="flex items-center gap-1">
                <DataSourceBadge source="actual" /> Recorded facts
              </span>
              <span className="flex items-center gap-1">
                <DataSourceBadge source="estimated" /> Calculated from data
              </span>
              <span className="flex items-center gap-1">
                <DataSourceBadge source="simulated" /> Pilot mode simulation
              </span>
              {data?.data_sources?.note && (
                <span className="text-slate-500 italic">{data.data_sources.note}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-20 text-slate-400">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No financial data available</p>
          </div>
        )}
      </main>
    </div>
  );
}
